import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import { getAds, deleteAd, toggleAd } from "../../services/adsService";

const BASE = "https://opiniongames.net/";
const toastCfg = { autoClose: 1800 };

const PAGE_LABELS = { home: "Home", betting: "Betting", match: "Match" };
const PAGE_COLORS = {
  home:    { bg: "#dbeafe", color: "#1d4ed8", border: "#bfdbfe" },
  betting: { bg: "#fef9c3", color: "#854d0e", border: "#fef08a" },
  match:   { bg: "#dcfce7", color: "#166534", border: "#bbf7d0" },
};

const s = {
  root:       { padding: "20px 24px 40px", background: "#f1f5f9", minHeight: "100vh", fontFamily: "'Inter', sans-serif" },
  headerWrap: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap", gap: 12 },
  pageTitle:  { margin: 0, fontWeight: 800, fontSize: "1.5rem", color: "#0f172a" },
  pageSub:    { margin: "4px 0 0", color: "#64748b", fontSize: "0.85rem" },
  addBtn:     { display: "flex", alignItems: "center", gap: 6, padding: "10px 20px", background: "linear-gradient(135deg,#16632b,#1da144)", border: "none", borderRadius: 9, fontFamily: "inherit", fontSize: "0.85rem", fontWeight: 700, color: "#fff", cursor: "pointer", textDecoration: "none" },
  filterRow:  { display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" },
  filterBtn:  { padding: "7px 16px", border: "1.5px solid #e2e8f0", borderRadius: 20, background: "#fff", fontSize: "0.8rem", fontWeight: 600, cursor: "pointer", color: "#475569", transition: "all 0.15s" },
  filterBtnA: { padding: "7px 16px", border: "1.5px solid #1da144", borderRadius: 20, background: "#dcfce7", fontSize: "0.8rem", fontWeight: 700, cursor: "pointer", color: "#166534" },
  table:      { width: "100%", borderCollapse: "collapse" },
  th:         { background: "#f8fafc", padding: "11px 14px", textAlign: "left", fontSize: "0.72rem", fontWeight: 800, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em", borderBottom: "2px solid #e8edf2", whiteSpace: "nowrap" },
  td:         { padding: "12px 14px", fontSize: "0.84rem", color: "#334155", borderBottom: "1px solid #f1f5f9", verticalAlign: "middle" },
  card:       { background: "#fff", borderRadius: 16, border: "1px solid #e8edf2", boxShadow: "0 1px 4px rgba(0,0,0,0.04)", overflow: "hidden" },
  imgThumb:   { width: 80, height: 46, objectFit: "cover", borderRadius: 6, border: "1px solid #e2e8f0", background: "#f1f5f9" },
  badge:      (page) => ({ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 10px", borderRadius: 100, fontSize: "0.7rem", fontWeight: 800, textTransform: "uppercase", border: `1px solid ${PAGE_COLORS[page]?.border || '#e2e8f0'}`, background: PAGE_COLORS[page]?.bg || '#f1f5f9', color: PAGE_COLORS[page]?.color || '#334155' }),
  activeBadge:   { display: "inline-flex", alignItems: "center", gap: 4, background: "#dcfce7", color: "#16a34a", padding: "3px 10px", borderRadius: 100, fontSize: "0.7rem", fontWeight: 800, border: "1px solid #bbf7d0" },
  inactiveBadge: { display: "inline-flex", alignItems: "center", gap: 4, background: "#fee2e2", color: "#dc2626", padding: "3px 10px", borderRadius: 100, fontSize: "0.7rem", fontWeight: 800, border: "1px solid #fecaca" },
  iconBtn:    { border: "none", cursor: "pointer", borderRadius: 7, padding: "5px 8px", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 16, transition: "opacity 0.15s" },
  empty:      { textAlign: "center", padding: "56px 20px", color: "#94a3b8" },
};

export const AdsListLayer = () => {
  const [ads, setAds]         = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageFilter, setPageFilter] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const [togglingId, setTogglingId] = useState(null);

  useEffect(() => { load(); }, [pageFilter]);

  const load = () => {
    setLoading(true);
    getAds(pageFilter)
      .then((res) => { if (res?.status) setAds(res.data ?? []); })
      .catch(() => toast.error("Failed to load ads", toastCfg))
      .finally(() => setLoading(false));
  };

  const handleDelete = (id) => {
    if (!window.confirm("Delete this ad?")) return;
    setDeletingId(id);
    deleteAd(id)
      .then((res) => {
        if (res?.status) {
          toast.success("Ad deleted", toastCfg);
          setAds((prev) => prev.filter((a) => a.id !== id));
        } else {
          toast.error(res?.msg || "Delete failed", toastCfg);
        }
      })
      .catch(() => toast.error("Delete failed", toastCfg))
      .finally(() => setDeletingId(null));
  };

  const handleToggle = (id) => {
    setTogglingId(id);
    toggleAd(id)
      .then((res) => {
        if (res?.status) {
          toast.success(res.msg, toastCfg);
          setAds((prev) => prev.map((a) => a.id === id ? { ...a, is_active: res.is_active } : a));
        } else {
          toast.error(res?.msg || "Toggle failed", toastCfg);
        }
      })
      .catch(() => toast.error("Toggle failed", toastCfg))
      .finally(() => setTogglingId(null));
  };

  const filters = [
    { label: "All Pages", value: "" },
    { label: "Home",      value: "home" },
    { label: "Betting",   value: "betting" },
    { label: "Match",     value: "match" },
  ];

  return (
    <div style={s.root}>
      {/* ── Header ── */}
      <div style={s.headerWrap}>
        <div>
          <h4 style={s.pageTitle}>Advertisement Management</h4>
          <p style={s.pageSub}>Manage banner ads across Home, Betting, and Match pages with slideshow support</p>
        </div>
        <Link to="/add-ad" style={s.addBtn}>
          <Icon icon="solar:add-circle-bold" style={{ fontSize: 16 }} />
          Add New Ad
        </Link>
      </div>

      {/* ── Page filter ── */}
      <div style={s.filterRow}>
        {filters.map((f) => (
          <button
            key={f.value}
            style={pageFilter === f.value ? s.filterBtnA : s.filterBtn}
            onClick={() => setPageFilter(f.value)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* ── Table card ── */}
      <div style={s.card}>
        {loading ? (
          <div style={{ ...s.empty }}>
            <span className="spinner-border spinner-border-sm" style={{ color: "#1da144", marginRight: 8 }} />
            Loading ads…
          </div>
        ) : ads.length === 0 ? (
          <div style={s.empty}>
            <Icon icon="solar:advertisement-bold-duotone" style={{ fontSize: 40, color: "#cbd5e1", display: "block", margin: "0 auto 12px" }} />
            No ads found
            {pageFilter && <p style={{ marginTop: 6, fontSize: "0.8rem" }}>Try removing the page filter</p>}
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>#</th>
                  <th style={s.th}>Preview</th>
                  <th style={s.th}>Title</th>
                  <th style={s.th}>Page</th>
                  <th style={s.th}>Size (W×H)</th>
                  <th style={s.th}>Slide (s)</th>
                  <th style={s.th}>Order</th>
                  <th style={s.th}>Status</th>
                  <th style={s.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {ads.map((ad) => (
                  <tr key={ad.id}>
                    <td style={s.td}>{ad.id}</td>
                    <td style={s.td}>
                      <img
                        src={ad.image_url}
                        alt={ad.title}
                        style={s.imgThumb}
                        onError={(e) => { e.target.src = "https://placehold.co/80x46?text=Ad"; }}
                      />
                    </td>
                    <td style={s.td}>
                      <div style={{ fontWeight: 600, color: "#0f172a" }}>{ad.title}</div>
                      {ad.click_url && (
                        <div style={{ fontSize: "0.72rem", color: "#94a3b8", marginTop: 2, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {ad.click_url}
                        </div>
                      )}
                    </td>
                    <td style={s.td}>
                      <span style={s.badge(ad.page)}>{PAGE_LABELS[ad.page] || ad.page}</span>
                    </td>
                    <td style={s.td}>{ad.width} × {ad.height}</td>
                    <td style={s.td}>{ad.slideshow_duration}s</td>
                    <td style={s.td}>{ad.display_order}</td>
                    <td style={s.td}>
                      <span style={ad.is_active ? s.activeBadge : s.inactiveBadge}>
                        <Icon icon={ad.is_active ? "solar:check-circle-bold" : "solar:close-circle-bold"} style={{ fontSize: 11 }} />
                        {ad.is_active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td style={s.td}>
                      <div style={{ display: "flex", gap: 6 }}>
                        {/* Edit */}
                        <Link
                          to={`/add-ad/${ad.id}`}
                          style={{ ...s.iconBtn, background: "#eff6ff", color: "#2563eb" }}
                          title="Edit"
                        >
                          <Icon icon="solar:pen-bold-duotone" />
                        </Link>

                        {/* Toggle active */}
                        <button
                          style={{ ...s.iconBtn, background: ad.is_active ? "#fef9c3" : "#dcfce7", color: ad.is_active ? "#854d0e" : "#166534", opacity: togglingId === ad.id ? 0.6 : 1 }}
                          onClick={() => handleToggle(ad.id)}
                          disabled={togglingId === ad.id}
                          title={ad.is_active ? "Deactivate" : "Activate"}
                        >
                          {togglingId === ad.id
                            ? <span className="spinner-border spinner-border-sm" style={{ width: 13, height: 13 }} />
                            : <Icon icon={ad.is_active ? "solar:eye-closed-bold" : "solar:eye-bold"} />
                          }
                        </button>

                        {/* Delete */}
                        <button
                          style={{ ...s.iconBtn, background: "#fee2e2", color: "#dc2626", opacity: deletingId === ad.id ? 0.6 : 1 }}
                          onClick={() => handleDelete(ad.id)}
                          disabled={deletingId === ad.id}
                          title="Delete"
                        >
                          {deletingId === ad.id
                            ? <span className="spinner-border spinner-border-sm" style={{ width: 13, height: 13 }} />
                            : <Icon icon="solar:trash-bin-trash-bold" />
                          }
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
